import { Package } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { formatGiftValue } from "@/lib/format-gift-value"
import { cn } from "@/lib/utils"
import type { Locale } from "@/types"
import type { CollabProduct } from "@/types/collab"

/**
 * OKS-482 — Shared product-display block, rendered on every gifting state
 * (confirm-address / preparing / shipped / delivered).
 *
 * 60×60 product photo (left) + name (semibold) · caption (`product_info`,
 * muted) · `Qty {n} · value about {value}` in the accent colour. Photo absent →
 * neutral placeholder tile.
 *
 * OKS-1000 — the value clause is omitted when the server resolved
 * `value_source: "none"`, and takes the "value from" wording when the resolved
 * amount is a floor (`value_is_range`). The amount itself is never re-derived
 * here; `MissionProduct#effective_value` is the single resolution.
 *
 * `product` may be null (mission has no structured product) — the block then
 * degrades to the caption line only, driven by the page's `product_info`
 * fallback passed as `captionFallback`.
 */
interface ProductBlockProps {
  product: CollabProduct | null | undefined
  /** `mission.product_info` fallback when there is no structured product. */
  captionFallback: string | null
  locale: Locale
}

export function ProductBlock({
  product,
  captionFallback,
  locale,
}: ProductBlockProps) {
  const { t } = useTranslation()
  // OKS-986 — a URL that FAILS TO LOAD (rehost rot, signed-URL expiry, 404)
  // degrades to the same neutral tile the never-had-a-photo case uses, rather
  // than the browser's broken-image glyph. Distinct from `photo_url === null`
  // and the easier of the two to forget. Mirrors `MissionCard`'s shipped
  // `coverErrored` pattern; same box either way, so no layout shift.
  const [photoErrored, setPhotoErrored] = useState(false)

  const name = product?.name ?? null
  const caption = product?.caption ?? captionFallback
  const photoUrl = photoErrored ? null : (product?.photo_url ?? null)
  // ⚠️ Branch on the SOURCE, never on the amount (D5): `0` is a legal `mission`
  // value and is falsy in JS.
  const value =
    product == null || product.value_source === "none"
      ? null
      : formatGiftValue(
          product.effective_value_cents,
          product.effective_currency,
          locale,
        )

  // No structured product AND no caption → nothing meaningful to show.
  if (!name && !caption) return null

  return (
    <div
      className="bg-muted/40 flex items-center gap-3 rounded-xl p-3"
      data-testid="gifting-product-block"
    >
      <div
        className={cn(
          "flex size-[60px] shrink-0 items-center justify-center overflow-hidden rounded-lg",
          !photoUrl && "bg-muted text-muted-foreground",
        )}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name ?? ""}
            className="size-full object-cover"
            data-testid="gifting-product-photo"
            onError={() => setPhotoErrored(true)}
          />
        ) : (
          <Package className="size-6" aria-hidden="true" />
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        {name && (
          <span
            className="truncate text-sm font-semibold"
            data-testid="gifting-product-name"
          >
            {name}
          </span>
        )}
        {caption && (
          <span className="text-muted-foreground truncate text-xs">
            {caption}
          </span>
        )}
        {product && (
          <span
            className="text-primary text-xs font-medium"
            data-testid="gifting-product-meta"
          >
            {value
              ? t(
                  product.value_is_range
                    ? "creator_mission_detail.gifting.qty_value_from"
                    : "creator_mission_detail.gifting.qty_value",
                  { count: product.quantity, value },
                )
              : t("creator_mission_detail.gifting.qty_only", {
                  count: product.quantity,
                })}
          </span>
        )}
        {/* OKS-1040 (R1c) — the picked variant. The server sends it only on a
            multi-product drop whose pick carries one, so single-product
            surfaces are unchanged (AC4). Wraps rather than truncates. */}
        {product?.variant_title && (
          <span
            className="text-muted-foreground text-xs break-words"
            data-testid="gifting-product-variant"
          >
            {t("creator_mission_detail.gifting.chosen_variant", {
              variant: product.variant_title,
            })}
          </span>
        )}
      </div>
    </div>
  )
}
